import type { TemplateInfo } from '@kit/bookings';

/**
 * A booking type presents as a *service list* (named rows + price) instead of
 * the duration-chip picker when its templates are ambiguous at the same
 * duration — i.e. the duration chips alone can't tell the customer which
 * template (and price) they're picking.
 *
 * The shape of that ambiguity differs by booking type:
 *
 *   • Court bookings — ambiguous only when two same-duration templates share a
 *     physical court. If each template owns a disjoint set of courts (e.g.
 *     Indoor → courts 1–6, Outdoor → courts 7–9), the downstream court picker
 *     already disambiguates template + price, so the duration-chip flow is
 *     enough and the service list would just be an extra step. When the same
 *     court is sold under two same-duration templates at different prices, the
 *     court pick can't resolve which one the customer wants — that's the case
 *     the service list exists for.
 *
 *   • Trainer / ball-machine bookings — there IS a downstream resource picker
 *     (the coach / ball-machine list, which shows each resource's own price), so
 *     these mirror the court case: a same-duration collision only needs the
 *     service list when two templates share a coach (the picker can't tell their
 *     prices apart). When the colliding templates are backed by DISJOINT coaches
 *     (e.g. "Private — Asaad & Hadi" @330 vs "Private" @400, no coach in both),
 *     picking the coach uniquely resolves template + price, so the duration-chip
 *     flow is enough and the service list would just be an extra step.
 *
 * Pass templates already filtered to the active sport + booking type.
 */

/**
 * True when two or more templates share a duration. The base ambiguity signal,
 * used directly for trainer/ball-machine flows and as the first gate for courts.
 */
export function hasDurationCollision(
  templates: Pick<TemplateInfo, 'duration'>[] | undefined,
): boolean {
  if (!templates || templates.length < 2) return false;

  const seen = new Set<number>();
  for (const tmpl of templates) {
    if (seen.has(tmpl.duration)) return true;
    seen.add(tmpl.duration);
  }
  return false;
}

/**
 * True when at least two templates in the list belong to the same service
 * option group (see TemplateInfo.variant_group). A lone member whose siblings
 * were filtered out (other location, members_only) is not a group here — it
 * renders as a plain row.
 */
export function hasServiceOptionGroup(
  templates: Pick<TemplateInfo, 'variant_group'>[] | undefined,
): boolean {
  if (!templates || templates.length < 2) return false;

  const seen = new Set<string>();
  for (const tmpl of templates) {
    const groupId = tmpl.variant_group?.id;
    if (!groupId) continue;
    if (seen.has(groupId)) return true;
    seen.add(groupId);
  }
  return false;
}

/**
 * True when two or more same-duration templates are assigned to the same court.
 * This is the court-specific ambiguity the service list resolves: a shared court
 * means the court picker can't tell the templates apart.
 *
 * Templates with no `court_ids` (older data / not yet populated) are treated as
 * unknown and fall back to a plain duration collision so behavior never silently
 * regresses to "always duration chips" for a tenant that genuinely needs the
 * service list.
 */
function hasSharedCourtCollision(templates: TemplateInfo[]): boolean {
  const byDuration = new Map<number, TemplateInfo[]>();
  for (const tmpl of templates) {
    const bucket = byDuration.get(tmpl.duration);
    if (bucket) bucket.push(tmpl);
    else byDuration.set(tmpl.duration, [tmpl]);
  }

  for (const bucket of byDuration.values()) {
    if (bucket.length < 2) continue;

    // If any colliding template is missing court_ids we can't prove the sets
    // are disjoint — fall back to treating the duration collision as ambiguous.
    if (bucket.some((t) => !t.court_ids || t.court_ids.length === 0)) {
      return true;
    }

    const seenCourtIds = new Set<string>();
    for (const tmpl of bucket) {
      for (const courtId of tmpl.court_ids ?? []) {
        if (seenCourtIds.has(courtId)) return true;
        seenCourtIds.add(courtId);
      }
    }
  }

  return false;
}

/**
 * True when two or more same-duration templates are backed by the same coach.
 * The trainer analogue of hasSharedCourtCollision: a shared coach means the
 * downstream coach picker can't tell the templates (and their prices) apart, so
 * the named service list is required. Disjoint coaches → the coach pick itself
 * disambiguates and the duration-chip flow is enough.
 *
 * Templates with no `trainer_ids` (older data / not yet populated by the RPC)
 * are treated as unknown and fall back to a plain duration collision so behavior
 * never silently regresses to "always duration chips" for a tenant that needs
 * the service list.
 */
function hasSharedTrainerCollision(templates: TemplateInfo[]): boolean {
  const byDuration = new Map<number, TemplateInfo[]>();
  for (const tmpl of templates) {
    const bucket = byDuration.get(tmpl.duration);
    if (bucket) bucket.push(tmpl);
    else byDuration.set(tmpl.duration, [tmpl]);
  }

  for (const bucket of byDuration.values()) {
    if (bucket.length < 2) continue;

    // If any colliding template is missing trainer_ids we can't prove the coach
    // sets are disjoint — fall back to treating the duration collision as ambiguous.
    if (bucket.some((t) => !t.trainer_ids || t.trainer_ids.length === 0)) {
      return true;
    }

    const seenTrainerIds = new Set<string>();
    for (const tmpl of bucket) {
      for (const trainerId of tmpl.trainer_ids ?? []) {
        if (seenTrainerIds.has(trainerId)) return true;
        seenTrainerIds.add(trainerId);
      }
    }
  }

  return false;
}

/**
 * Business types that always use the duration-chip picker and never the named
 * service list — the venue is a pure court operator (padel) where the classic
 * "pick 60 / 90 min" flow is the expected entry point. Coaching templates at
 * these tenants can still share coaches at different prices, but the product
 * decision has always been to keep the duration chips as the top-level control
 * and let the downstream coach pick surface each coach's price, rather than
 * front-loading a named service list. Matched against `business_settings.business_type`.
 */
const DURATION_CHIP_ONLY_BUSINESS_TYPES = new Set(['padel_court']);

/**
 * Court operators sell one bookable space (a court, a field) at several
 * lengths, and name templates after the space ("Basketball Half Court · 60
 * min"). Their duration chips + court picker stay the entry point even when
 * template names differ.
 */
const COURT_OPERATOR_BUSINESS_TYPES = new Set(['padel_court', 'sports']);

const DURATION_TOKEN =
  /\d+(?:[.,]\d+)?\s*(?:minutes?|mins?|min|hours?|hrs?|hr|h|دقيقة|دقائق|ساعة|ساعات)\.?/giu;

/**
 * A template's name without its length ("4D Massage · 30 min" → "4d massage",
 * "60 min" → ""), so lengths of one service compare equal.
 */
function serviceBaseName(name: string): string {
  return name
    .replace(DURATION_TOKEN, ' ')
    .replace(/[()·•|:,\-–—]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .toLowerCase();
}

/**
 * True when the templates are different services rather than lengths of one
 * service: e.g. "Ice Bath" 15 min, "Brain Gym" 25 min, "Infrared Sauna" 45
 * min, each in its own room. Duration chips would show "15 Min / 25 Min /
 * 45 Min" and hide what the customer is booking. Names that are only a length
 * ("60 min") carry no service and are ignored.
 */
export function hasDistinctServiceNames(
  templates: Pick<TemplateInfo, 'name'>[] | undefined,
): boolean {
  if (!templates || templates.length < 2) return false;

  const names = new Set<string>();
  for (const tmpl of templates) {
    const base = serviceBaseName(tmpl.name);
    if (base) names.add(base);
    if (names.size > 1) return true;
  }
  return false;
}

/**
 * Decide whether the given booking type should render the named service list
 * (vs the duration-chip picker). Pass templates already filtered to the active
 * sport + booking type, that booking type, and the tenant's business type.
 *
 * The booking type must be passed explicitly rather than inferred from the
 * templates' `has_court_booking` flag: the three booking flags are independent
 * (a coaching template runs on a court, so it has BOTH `has_court_booking` and
 * `has_trainer_booking` true). Inferring "court flow" from `has_court_booking`
 * would route a coach booking through the shared-court check and wrongly
 * suppress the service list for same-duration coach templates on disjoint
 * courts — the exact ambiguity the trainer branch must keep showing.
 *
 * `businessType` short-circuits the whole decision: pure court operators
 * (see DURATION_CHIP_ONLY_BUSINESS_TYPES) always stay on the duration chips.
 */
export function shouldShowServiceList(
  bookingType: string,
  templates: TemplateInfo[] | undefined,
  businessType?: string | null,
): boolean {
  if (businessType && DURATION_CHIP_ONLY_BUSINESS_TYPES.has(businessType)) {
    return false;
  }

  if (!templates || templates.length < 2) return false;

  // A tenant that has grouped templates into a service with options has said
  // outright that the named list is how these should be picked — no need to
  // infer it from resource collisions. Duration chips can't show a group.
  if (hasServiceOptionGroup(templates)) return true;

  // A service business that gives each service its own room (court) has no
  // duration collision, yet chips alone can't say which service is which.
  if (
    bookingType === 'court' &&
    !(businessType && COURT_OPERATOR_BUSINESS_TYPES.has(businessType)) &&
    hasDistinctServiceNames(templates)
  ) {
    return true;
  }

  // Both flows have a downstream resource picker that already shows per-resource
  // price, so each can stay on duration chips when the colliding templates own
  // DISJOINT resources (the pick itself disambiguates template + price) and only
  // falls back to the service list when they SHARE a resource (the pick can't
  // tell the prices apart):
  //   • court        → disjoint/shared physical court (hasSharedCourtCollision)
  //   • trainer      → disjoint/shared coach          (hasSharedTrainerCollision)
  //   • ball_machine → disjoint/shared machine, same coach-availability backing
  // If trainer_ids are absent (older data) hasSharedTrainerCollision fails closed
  // to a plain duration collision, preserving the previous always-service-list
  // behavior until the RPC populates them.
  if (bookingType === 'court') return hasSharedCourtCollision(templates);
  return hasSharedTrainerCollision(templates);
}
